import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ExternalLink, FileDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { exportScoutingPlayerPdf } from "@/lib/export";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { PRIORITY_BADGE, STATUS_BADGE } from "@/components/ScoutingPlayersTable";
import ScoutingParticipation from "@/components/ScoutingParticipation";
import ScoutingRffmPanel from "@/components/ScoutingRffmPanel";

const PURPLE = "#5B2383";
const GOLD = "#D9B300";
const STATUSES = Object.keys(STATUS_BADGE);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso || ""; }
};

export default function ScoutingPlayerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [fuOpen, setFuOpen] = useState(false);
  const [editingFuId, setEditingFuId] = useState(null);
  const EMPTY_FU = {
    observation_date: "", match_or_training: "", technical_notes: "", tactical_notes: "",
    physical_notes: "", behavioral_notes: "", general_observation: "", recommendation: "", sporting_status: "",
  };
  const [fu, setFu] = useState(EMPTY_FU);

  const load = () => {
    api.get(`/scouting-market/players/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="text-slate-400 text-sm">Cargando...</div>;
  const p = data.player;

  const patch = async (body) => {
    try {
      await api.put(`/scouting-market/players/${id}`, body);
      toast.success("Guardado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveFollowup = async () => {
    try {
      if (editingFuId) {
        await api.put(`/scouting-market/players/${id}/followups/${editingFuId}`, fu);
        toast.success("Informe actualizado");
      } else {
        await api.post(`/scouting-market/players/${id}/followups`, fu);
        toast.success("Informe guardado");
      }
      setFuOpen(false);
      setEditingFuId(null);
      setFu(EMPTY_FU);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const editFollowup = (f) => {
    setFu({
      observation_date: f.observation_date || "", match_or_training: f.match_or_training || "",
      technical_notes: f.technical_notes || "", tactical_notes: f.tactical_notes || "",
      physical_notes: f.physical_notes || "", behavioral_notes: f.behavioral_notes || "",
      general_observation: f.general_observation || "", recommendation: f.recommendation || "",
      sporting_status: f.sporting_status || "",
    });
    setEditingFuId(f.id);
    setFuOpen(true);
  };

  const deleteFollowup = async (f) => {
    if (!window.confirm("¿Eliminar este informe presencial?")) return;
    try {
      await api.delete(`/scouting-market/players/${id}/followups/${f.id}`);
      toast.success("Informe eliminado");
      if (editingFuId === f.id) { setFuOpen(false); setEditingFuId(null); setFu(EMPTY_FU); }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const chartData = data.seasons.map((s) => ({
    name: s.season, PJ: s.matches_played || 0, TIT: s.starts || 0, Goles: s.goals || 0,
  }));

  return (
    <div className="space-y-5" data-testid="scouting-player-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="scouting-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver
      </Button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Ficha de scouting</div>
          <h1 className="text-2xl sm:text-3xl font-black" style={{ fontFamily: "Nunito" }}>{p.player_name}</h1>
          <div className="flex flex-wrap gap-1.5 mt-2 items-center">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
              {p.projected_category_computed || "Sin año confirmado"}
            </span>
            {p.birth_year && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Año {p.birth_year}</span>}
            {p.position && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p.position}</span>}
            {p.role && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p.role}</span>}
            {p.priority && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIORITY_BADGE[p.priority] || "bg-slate-100 text-slate-600"}`}>{p.priority}</span>}
            {p.rffm_status && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">RFFM: {p.rffm_status}</span>}
            {p.added_by && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="detail-added-by">Añadido por {p.added_by}</span>}
          </div>
          <div className="text-sm text-slate-500 mt-2">
            {p.current_team ? <>Equipo 25/26: <b className="text-slate-700">{p.current_team}</b>{p.current_category ? ` · ${p.current_category}` : ""}</> :
              p.reported_team ? <>Equipo declarado: <b className="text-slate-700">{p.reported_team}</b>{p.reported_category ? ` · ${p.reported_category}` : ""}</> : null}
          </div>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (!window.confirm(`¿Eliminar definitivamente a "${p.player_name}" del scouting? Se borrarán también sus temporadas y su seguimiento.`)) return;
                try {
                  await api.delete(`/scouting-market/players/${p.id}`);
                  toast.success(`${p.player_name} eliminado`);
                  navigate("/scouting?tab=jugadores");
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              className="border-rose-200 text-rose-600 hover:bg-rose-50 font-bold"
              data-testid="scouting-player-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportScoutingPlayerPdf(p)}
            className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
            data-testid="scouting-player-pdf-btn"
          >
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          {p.rffm_url && (
            <a href={p.rffm_url} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="detail-rffm-link">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> RFFM
              </Button>
            </a>
          )}
          {p.external_url && (
            <a href={p.external_url} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Fuente externa</Button>
            </a>
          )}
        </div>
      </div>

      {/* KPIs acumulados */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ["Convocatorias 3T", p.callups_3_seasons],
          ["Partidos 3T", p.matches_3_seasons],
          ["Titularidades 3T", p.starts_3_seasons],
          ["Goles 3T", p.goals_3_seasons],
          ["Índice", p.scouting_score],
        ].map(([label, v], i) => (
          <Card key={label} className="border-slate-200 shadow-sm">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-black" style={{ fontFamily: "Nunito", color: i === 4 ? GOLD : PURPLE }}>
                {v ?? "—"}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Motor RFFM: código + informe 3 temporadas + actualización */}
      <ScoutingRffmPanel player={p} isAdmin={isAdmin} onUpdated={load} />

      {/* Estado deportivo + prioridad (solo admin edita) */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Estado deportivo:</div>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => (
              <button
                key={s}
                disabled={!isAdmin}
                onClick={() => patch({ sporting_status: s })}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${
                  p.sporting_status === s ? "text-white" : `${STATUS_BADGE[s]} ${isAdmin ? "hover:opacity-75" : "opacity-60"}`
                }`}
                style={p.sporting_status === s ? { backgroundColor: PURPLE } : {}}
                data-testid={`detail-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Histórico 3 temporadas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Histórico RFFM (3 temporadas)</div>
            {data.seasons.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-4">Sin datos oficiales RFFM para este jugador.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 pr-2">Temporada</th><th className="py-1.5 pr-2">Equipo</th>
                      <th className="py-1.5 pr-2">Categoría</th><th className="py-1.5 text-right">CONV</th>
                      <th className="py-1.5 text-right">TIT</th><th className="py-1.5 text-right">SUP</th>
                      <th className="py-1.5 text-right">PJ</th><th className="py-1.5 text-right">G</th>
                      <th className="py-1.5 text-right">🟨</th><th className="py-1.5 text-right">🟥</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.seasons.map((s) => (
                      <tr key={s.season} data-testid={`season-row-${s.season}`}>
                        <td className="py-1.5 pr-2 font-bold whitespace-nowrap">{s.season}</td>
                        <td className="py-1.5 pr-2 max-w-[140px] truncate">{s.team || "—"}</td>
                        <td className="py-1.5 pr-2 max-w-[120px] truncate">{s.category || "—"}</td>
                        <td className="py-1.5 text-right">{s.callups ?? "—"}</td>
                        <td className="py-1.5 text-right">{s.starts ?? "—"}</td>
                        <td className="py-1.5 text-right">{s.substitute_appearances ?? "—"}</td>
                        <td className="py-1.5 text-right font-bold">{s.matches_played ?? "—"}</td>
                        <td className="py-1.5 text-right font-bold text-purple-800">{s.goals ?? "—"}</td>
                        <td className="py-1.5 text-right">{s.yellow_cards ?? 0}</td>
                        <td className="py-1.5 text-right">{s.red_cards ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">Evolución por temporada</div>
            {chartData.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-4">Sin datos para el gráfico.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="PJ" fill={PURPLE} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="TIT" fill="#9F67D8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Goles" fill={GOLD} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Minutos y participación RFFM */}
      <ScoutingParticipation playerId={id} seasons={data.seasons} />

      {/* Observaciones + procedencia */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Observaciones internas</div>
            <ObservationsEditor value={p.observations || ""} canEdit={isAdmin} onSave={(v) => patch({ observations: v })} />
            {p.source_documents && (
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                <b>Procedencia:</b> {p.source_documents}
              </div>
            )}
            {p.official_rffm_name && (
              <div className="text-xs text-slate-500"><b>Nombre oficial RFFM:</b> {p.official_rffm_name}{p.name_similarity ? ` (similitud ${p.name_similarity})` : ""}</div>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Historial de cambios</div>
            {(p.change_log || []).length === 0 ? (
              <div className="text-sm text-slate-400 italic">Sin cambios registrados.</div>
            ) : (
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {[...(p.change_log || [])].reverse().map((c, i) => (
                  <div key={i} className="text-xs text-slate-600">
                    <span className="font-bold">{fmtDate(c.date)}</span> · {c.event}
                    {c.fields?.length ? <span className="text-slate-400"> ({c.fields.join(", ")})</span> : null}
                    {c.by ? <span className="text-slate-400"> · {c.by}</span> : null}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Informes presenciales */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Informes presenciales ({data.followups.length})</div>
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (fuOpen) { setFuOpen(false); setEditingFuId(null); setFu(EMPTY_FU); }
                  else setFuOpen(true);
                }}
                data-testid="followup-new-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> {fuOpen ? "Cerrar" : "Nuevo informe"}
              </Button>
            )}
          </div>

          {fuOpen && (
            <div className="rounded-md border border-purple-100 bg-purple-50/30 p-3 space-y-2" data-testid="followup-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input type="date" value={fu.observation_date} onChange={(e) => setFu({ ...fu, observation_date: e.target.value })} data-testid="fu-date" />
                <Input placeholder="Partido o entrenamiento observado" value={fu.match_or_training} onChange={(e) => setFu({ ...fu, match_or_training: e.target.value })} data-testid="fu-match" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Textarea rows={2} placeholder="Aspectos técnicos" value={fu.technical_notes} onChange={(e) => setFu({ ...fu, technical_notes: e.target.value })} data-testid="fu-tech" />
                <Textarea rows={2} placeholder="Aspectos tácticos" value={fu.tactical_notes} onChange={(e) => setFu({ ...fu, tactical_notes: e.target.value })} data-testid="fu-tact" />
                <Textarea rows={2} placeholder="Aspectos físicos" value={fu.physical_notes} onChange={(e) => setFu({ ...fu, physical_notes: e.target.value })} data-testid="fu-phys" />
                <Textarea rows={2} placeholder="Comportamiento / actitud" value={fu.behavioral_notes} onChange={(e) => setFu({ ...fu, behavioral_notes: e.target.value })} data-testid="fu-behav" />
              </div>
              <Textarea rows={2} placeholder="Observación general" value={fu.general_observation} onChange={(e) => setFu({ ...fu, general_observation: e.target.value })} data-testid="fu-general" />
              <div className="flex flex-wrap gap-2 items-center">
                <Input placeholder="Recomendación" value={fu.recommendation} onChange={(e) => setFu({ ...fu, recommendation: e.target.value })} className="flex-1 min-w-[180px]" data-testid="fu-recommendation" />
                <select
                  value={fu.sporting_status}
                  onChange={(e) => setFu({ ...fu, sporting_status: e.target.value })}
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold"
                  data-testid="fu-status"
                >
                  <option value="">Mantener estado</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <Button onClick={saveFollowup} className="text-white font-bold" style={{ backgroundColor: PURPLE }} data-testid="fu-save">
                  {editingFuId ? "Actualizar informe" : "Guardar informe"}
                </Button>
              </div>
            </div>
          )}

          {data.followups.length === 0 ? (
            <div className="text-sm text-slate-400 italic">Aún no hay informes presenciales.</div>
          ) : (
            <div className="space-y-2">
              {data.followups.map((f) => (
                <div key={f.id} className="rounded-md border border-slate-200 p-3 text-sm space-y-1" data-testid={`followup-${f.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="font-bold text-slate-800">{f.match_or_training || "Observación"}</div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-slate-400">{f.observation_date || fmtDate(f.created_at)} · {f.author_name}</div>
                      {isAdmin && (
                        <>
                          <button
                            type="button"
                            onClick={() => editFollowup(f)}
                            className="text-[11px] font-bold text-purple-700 hover:underline"
                            data-testid={`followup-edit-${f.id}`}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteFollowup(f)}
                            className="text-[11px] font-bold text-rose-600 hover:underline"
                            data-testid={`followup-delete-${f.id}`}
                          >
                            Borrar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {[["Técnico", f.technical_notes], ["Táctico", f.tactical_notes], ["Físico", f.physical_notes], ["Actitud", f.behavioral_notes], ["General", f.general_observation]]
                    .filter(([, v]) => v)
                    .map(([k, v]) => <div key={k} className="text-xs text-slate-600"><b className="text-purple-800">{k}:</b> {v}</div>)}
                  {f.recommendation && (
                    <div className="text-xs font-bold" style={{ color: GOLD }}>Recomendación: {f.recommendation}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ObservationsEditor({ value, canEdit, onSave }) {
  const [v, setV] = useState(value);
  const [editing, setEditing] = useState(false);
  useEffect(() => setV(value), [value]);
  if (!editing) {
    return (
      <div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{value || <span className="text-slate-400 italic">Sin observaciones.</span>}</p>
        {canEdit && (
          <Button size="sm" variant="ghost" className="mt-1 text-xs" onClick={() => setEditing(true)} data-testid="obs-edit-btn">Editar</Button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Textarea rows={4} value={v} onChange={(e) => setV(e.target.value)} data-testid="obs-textarea" />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => { onSave(v); setEditing(false); }} className="bg-purple-700 text-white" data-testid="obs-save-btn">Guardar</Button>
        <Button size="sm" variant="outline" onClick={() => { setV(value); setEditing(false); }}>Cancelar</Button>
      </div>
    </div>
  );
}
